/**
 * symbolUtils
 * -----------------
 * These functions are used internally by the Symbol components
 * to translate Symbol and/or Conditional State configuration to
 * React/HTML Element Attributes (`onClick`, `style`, etc).
 */
import { __assign } from "tslib";
import { fireClickTracking } from 'shared/utils/fireClickTracking';
import { first } from 'shared/utils/misc';
import { ParsedURL } from 'shared/utils/urls/ParsedURL';
import { processClickTagUrl, validUrlOrUndefined, } from 'shared/utils/urls/processClickTagUrl';
/**
 * Process and merge styles from attribute map
 *
 * @param attributes attributes from conditionState
 * @param attributeMap symbol-specific map of styles for a certain attribute value
 */
export var applyAttributeStyles = function (attributes, attributeMap) {
    return Object.keys(attributeMap)
        // our AttributeStyleMap only applies to string values
        .filter(function (k) { return attributes[k] && typeof attributes[k] === 'string'; })
        .map(function (k) { return attributeMap[k][attributes[k]]; })
        .reduce(function (acc, style) { return (__assign(__assign({}, acc), style)); }, {});
};
/**
 * Returns mouse event related props (onClick event and styles).
 *
 * @param destinationUrl Optional click-thru URL.
 * @param disabled Only specifically set (`true` or `false`) in the editor. `undefined` elsewhere.
 * @param hoverAnimation If a hover animation is provided, pointer events must be enabled.
 */
export var mouseEventProps = function (symbol, disabled, hoverAnimation) {
    // If `disabled` is defined, we are in-editor.
    if (typeof disabled !== 'undefined') {
        return {
            mouseEventStyles: { pointerEvents: disabled ? 'none' : 'auto' },
        };
    }
    // Use `ct` query param override on Stage click-through if available.
    var destinationUrl = getDestinationUrl(symbol);
    if (destinationUrl) {
        return clickThruProps(destinationUrl);
    }
    return {
        mouseEventStyles: {
            // If there is no hoverAnimation, disabled pointer events
            // in order to allow Symbols with a lower z-index to receive
            // mouseEnter/mouseLeave events.
            pointerEvents: hoverAnimation ? 'auto' : 'none',
        },
    };
};
/**
 * Get correct destination URL. The default click URL
 * follows this order of priority:
 *
 * - 1st: Paramount Tracking clickThru Url definition
 * - 2nd: `ct` params
 * - 3rd: `symbol.clickUrl`
 */
var getDestinationUrl = function (symbol) {
    if (symbol.type !== 'stage') {
        return symbol.clickUrl;
    }
    return window.paramountClickUrl || getExternalClickTag() || symbol.clickUrl;
};
/**
 * Get any external click tag override, if available.
 */
var getExternalClickTag = function () {
    return validUrlOrUndefined(first(getExternalParameters().ct));
};
/**
 * Get any external tracking parameters. For iFramed ads, these are
 * passed through as query parameters. For inline/HTML5 ads, these
 * are attached to `window.ADAPT_TRACKING`.
 */
var getExternalParameters = function () {
    return window.ADAPT_TRACKING || new ParsedURL().queryParams;
};
/**
 * Returns the click handler and styles necessary for a
 * Symbol to support click-to-url (with click tracking).
 */
var clickThruProps = function (destinationUrl) { return ({
    onClick: function (e) {
        e.preventDefault();
        e.stopPropagation();
        // Final URL is determined at time of click to account for changes since
        // last render. UTM overrides, query params, etc, may have been updated.
        fireClickTracking(processClickTagUrl(destinationUrl));
    },
    mouseEventStyles: {
        cursor: 'pointer',
        pointerEvents: 'auto',
    },
    clickClass: 'clickthru',
}); };
/**
 * Build correct css style for background
 * when combine with gradient and pattern image
 * see: https://jsfiddle.net/dfn30wqj/2/
 */
export var buildBackgroundStyle = function (styles) {
    var _a = styles.background, background = _a === void 0 ? '' : _a, _b = styles.backgroundColor, backgroundColor = _b === void 0 ? '' : _b;
    return [background, backgroundColor, 'repeat'].join(' ');
};
/**
 * Join and clean className
 * - remove all undefined and empty
 */
export var joinClass = function () {
    var arg = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        arg[_i] = arguments[_i];
    }
    return arg.filter(function (i) { return i; }).join(' ');
};
/**
 * Can this Symbol have children?
 */
export var isParent = function (symbol) {
    return ['scene', 'group', 'stage'].indexOf(symbol.type) !== -1;
};
